import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import CharacterMpProvider from "@/core/data-provider/data-mp/character/character-mp-provider.ts";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response.ts";
import { EstateSelectTab } from "@/domains/estate/constants/estateSelectTab";
import { setSlotsSelectedEstateId, slotsSelector } from "@/domains/estate/slice";
import toast from "@/domains/toasts/helpers/toast.ts";
import useLoadSlots from "@/domains/estate/hooks/useLoadSlots";
import EstateEstateSelectTabsContainer from "@/domains/estate/containers/tablet/slots/EstateSlotsTabsContainer";
import EstatePurchaseModal from "@/domains/estate/components/EstatePurchaseModal";
import EstateSlots from "@/domains/estate/components/slots/EstateSlots.tsx";
import EstateSlotsHeader from "@/domains/estate/components/slots/EstateSlotsHeader.tsx";
import EstateSlotsWrapper from "@/domains/estate/components/slots/EstateSlotsWrapper.tsx";

export default function EstateSlotsContainer() {
  const dispatch = useAppDispatch();
  const slots = useAppSelector(slotsSelector);
  const { t } = useTranslation("estate");
  const [pendingPurchase, setPendingPurchase] = useState(false);
  const [isPurchaseModal, setPurchaseModal] = useState(false);

  const { loading, loadSlots } = useLoadSlots();

  const purchaseSlot = async () => {
    setPendingPurchase(true);
    const response = await CharacterMpProvider.estateCapacityUpgrade();

    if (response instanceof RpcMpProviderFailedResponse) {
      toast.error(t(`estate.slots.errors.${response.error.message}`));
      setPendingPurchase(false);
      return;
    }

    toast.success(t("estate.slots.successCapacityUpgrade"));
    loadSlots();
    setPendingPurchase(false);
    setPurchaseModal(false);
  };

  const changeSelectedEstateId = useCallback(
    (id: number) => dispatch(setSlotsSelectedEstateId(id)),
    [dispatch],
  );

  const estates =
    slots.selectedTab === EstateSelectTab.Owner ? slots.ownerEstates : slots.tenantEstates;

  return (
    <EstateSlotsWrapper>
      {isPurchaseModal && (
        <EstatePurchaseModal
          onClose={() => setPurchaseModal(false)}
          onConfirm={purchaseSlot}
          isLoading={pendingPurchase}
          isConfirming={pendingPurchase}
          price={200}
        />
      )}
      <EstateEstateSelectTabsContainer />
      <EstateSlotsHeader count={estates.length} selectedTab={slots.selectedTab} />
      {loading ? (
        <div className="absolute z-10 top-1/2 left-1/2 flex items-center justify-center p-4 -translate-x-1/2 -translate-y-1/2">
          <div className="mz-loader" />
        </div>
      ) : (
        <EstateSlots
          estates={estates}
          changeSelectedEstateId={changeSelectedEstateId}
          count={EstateSelectTab.Owner ? slots.settings.ownerCount : slots.settings.tenantCount}
          maxCount={slots.settings.maxCount}
          setPurchaseModal={setPurchaseModal}
        />
      )}
    </EstateSlotsWrapper>
  );
}
